import React, { useState } from 'react';
import { Text } from '@adobe/react-spectrum';
import Edit from '@spectrum-icons/workflow/Edit';
import ChevronIcon from 'app/assets/svgIcons/ChevronIcon';
import styles from './PrimaryContacts.module.scss';
import slideOutStyles from '../SlideOutPanel.module.scss';
import EditPrimaryContact from './EditPrimaryContact';
import AddPrimaryContact from '../../AddPrimaryContact';
import Avatar from '../DACIContacts/Avatar';
import { EditOnlyWrapper } from '../context/RBACContext';

interface Contact {
  role: string;
  name: string;
  imageUrl?: string;
  type?: string;
  uid?: string;
  email?: string;
  department?: string;
}

interface PrimaryContactsProps {
  contacts: Contact[];
  onContactUpdate?: (updatedContact: Contact, index: number) => void;
  onContactAdd?: (newContact: Contact | Contact[]) => void;
  showHeader?: boolean;
  showChevron?: boolean;
  onExternalAddClick?: () => void;
  onRoleContactsReplace?: (role: string, contacts: Contact[]) => void;
}

const PrimaryContacts: React.FC<PrimaryContactsProps> = ({
  contacts,
  onContactUpdate = () => {}, // Default no-op function
  onContactAdd = () => {}, // Default no-op function
  showHeader = true,
  showChevron = false,
  onExternalAddClick,
  onRoleContactsReplace
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);

  // Expose the add functionality to external components
  React.useEffect(() => {
    if (onExternalAddClick) {
      const externalHandler = () => setIsAddPopupOpen(true);
      // Store the handler so it can be called from external button
      (window as any).primaryContactsAddHandler = externalHandler;
    }
    return () => {
      delete (window as any).primaryContactsAddHandler;
    };
  }, [onExternalAddClick]);

  // Helper function to generate avatar URL from UID (same pattern as DACI)
  const generateAvatarUrl = (contact: Contact): string => {
    if (contact.uid) {
      return `https://s7d2.scene7.com/is/image/IMGDIR/${contact.uid}`;
    }
    if (contact.imageUrl) {
      return contact.imageUrl;
    }
    return 'https://via.placeholder.com/32';
  };

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
  };

  const handleContactChange = (value: any, index: number) => {
    if (value && value.name) {
      // Create updated contact with LDAP user data
      const updatedContact = {
        ...contacts[index],
        name: value.name,
        uid: value.uid,
        email: value.email,
        department: value.department,
        type: 'ldap',
        imageUrl: value.uid
          ? `https://s7d2.scene7.com/is/image/IMGDIR/${value.uid}`
          : undefined
      };

      // Call the update handler
      onContactUpdate(updatedContact, index);
    }

    // Exit edit mode
    setEditingIndex(null);
  };

  const handleAddButtonClick = () => {
    setIsAddPopupOpen(true);
  };

  const handleAddPopupClose = () => {
    setIsAddPopupOpen(false);
  };

  const handleAddContact = (newContact: Contact | Contact[]) => {
    // Call the add handler
    try {
      onContactAdd(newContact);
    } catch (error) {
      console.error('PrimaryContacts: Error calling onContactAdd:', error);
    }
  };

  // Filter out any empty contacts (without name)
  const validContacts = contacts.filter(
    contact => contact.name && contact.name.trim() !== ''
  );

  return (
    <div
      data-testid="primary-contacts-container"
      className={styles.primaryContactsContainer}
    >
      {showHeader && (
        <div
          data-testid="primary-contacts-header"
          className={styles.primaryContactsHeader}
        >
          <Text
            data-testid="primary-contacts-title"
            UNSAFE_className={styles.title}
          >
            Primary Contacts
          </Text>
          {showChevron && (
            <div
              data-testid="primary-contacts-chevron"
              className={slideOutStyles.phaseToggle}
            >
              <ChevronIcon />
            </div>
          )}
          <EditOnlyWrapper>
            <button
              data-testid="primary-contacts-add-button"
              className={styles.addButton}
              onClick={handleAddButtonClick}
              aria-label="Add primary contact"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 3V13M3 8H13"
                  stroke="#909090"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </EditOnlyWrapper>
        </div>
      )}

      <div data-testid="primary-contacts-list" className={styles.contactList}>
        {validContacts.map((contact, index) => (
          <div
            key={`${contact.role}-${contact.uid || contact.name || index}`}
            data-testid={`primary-contact-item-${contact.role
              ?.toLowerCase()
              .replace(/\s+/g, '-')}-${index}`}
            className={styles.contactItem}
          >
            <div
              data-testid={`primary-contact-role-${contact.role
                ?.toLowerCase()
                .replace(/\s+/g, '-')}-${index}`}
              className={styles.roleLabel}
            >
              {contact.role}
            </div>
            {/* Always show the contact info */}
            {
              <div
                data-testid={`primary-contact-info-${index}`}
                className={styles.contactInfo}
              >
                <div
                  data-testid={`primary-contact-avatar-${index}`}
                  className={styles.profilePicture}
                >
                  <Avatar
                    src={generateAvatarUrl(contact)}
                    alt={`${contact.name}'s profile`}
                    name={contact.name}
                    size={32}
                  />
                </div>
                <div
                  data-testid={`primary-contact-details-${index}`}
                  className={styles.contactDetails}
                >
                  <div
                    data-testid={`primary-contact-name-container-${index}`}
                    className={styles.nameContainer}
                  >
                    <span
                      data-testid={`primary-contact-name-${index}`}
                      className={styles.name}
                    >
                      {contact.name}
                    </span>
                    <EditOnlyWrapper>
                      <button
                        data-testid={`primary-contact-edit-button-${index}`}
                        className={styles.editButton}
                        onClick={() => handleEditClick(index)}
                        aria-label={`Edit ${contact.name}`}
                      >
                        <Edit size="S" />
                      </button>
                    </EditOnlyWrapper>
                  </div>
                  {contact.type === 'ldap' && contact.email && (
                    <div
                      data-testid={`primary-contact-email-${index}`}
                      className={styles.email}
                    >
                      {contact.email}
                    </div>
                  )}
                  {contact.type === 'ldap' && contact.department && (
                    <div
                      data-testid={`primary-contact-department-${index}`}
                      className={styles.department}
                    >
                      {contact.department}
                    </div>
                  )}
                </div>
              </div>
            }
          </div>
        ))}
      </div>

      {/* Edit Primary Contact Modal */}
      {editingIndex !== null && validContacts[editingIndex] && (
        <EditPrimaryContact
          isOpen={true}
          contact={validContacts[editingIndex]}
          existingContacts={validContacts} // Pass ALL existing contacts for all roles
          onClose={() => setEditingIndex(null)}
          onSave={(role, updatedContacts) => {
            // New signature: (role: string, contacts: Contact[])
            if (onRoleContactsReplace) {
              // Delegate to parent for phased API diffing and update
              onRoleContactsReplace(role, updatedContacts);
              setEditingIndex(null);
              return;
            }

            // Fallback: local merge (non-phased usage)
            const kept = validContacts.filter(c => c.role !== role);
            const merged = [
              ...kept,
              ...updatedContacts.map(c => ({
                ...c,
                role
              }))
            ];
            const uniqueByUid = Array.from(
              new Map(
                merged.map(c => [c.uid || `${c.role}-${c.name}`, c])
              ).values()
            );
            uniqueByUid.forEach((c, idx) => onContactUpdate(c, idx));
            setEditingIndex(null);
          }}
          lastUpdated={undefined} // TODO: Add timestamp from API
          updatedBy={undefined} // TODO: Add user info from API
        />
      )}

      {/* Add Primary Contact Popup */}
      <AddPrimaryContact
        isOpen={isAddPopupOpen}
        onClose={handleAddPopupClose}
        onSave={handleAddContact}
        existingContacts={validContacts} // Pass existing contacts for duplicate prevention
      />
    </div>
  );
};

export default PrimaryContacts;
